import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Lock, Mail, User as UserIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { UserStatus } from '../types'

export const Route = createFileRoute('/login')({
    component: LoginRoute,
    validateSearch: (search: Record<string, unknown>) => {
        const rawRedirect = search.redirect
        const redirect = typeof rawRedirect === 'string' ? rawRedirect : '/home'
        return { redirect }
    },
})

function LoginRoute() {
    usePageTitle('Login')
    const navigate = useNavigate()
    const {
        isAuthenticated,
        isAdmin,
        signInWithGoogle,
        signInWithGoogleRedirect,
        signInWithEmail,
        signUpWithEmail,
        signOut,
        loading,
        userData,
    } = useAuth()

    const [isSigningIn, setIsSigningIn] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isSignUp, setIsSignUp] = useState(false)

    // Email/Password form state
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [displayName, setDisplayName] = useState('')

    const { redirect } = Route.useSearch()

    // Redirect if already authenticated and approved
    useEffect(() => {
        if (!loading && isAuthenticated && (userData?.status === UserStatus.ACTIVE || isAdmin)) {
            navigate({
                to: redirect,
                replace: true,
            })
        }
    }, [loading, isAuthenticated, userData, isAdmin, navigate, redirect])

    const handleGoogleSignIn = async () => {
        if (isSigningIn || loading) return // Prevent double-click
        try {
            setIsSigningIn(true)
            setError(null)

            // This will trigger a redirect
            const result = await signInWithGoogle()

            if (result.success && result.pending) {
                // Redirect happening...
                return
            }

            if (!result.success) {
                if (result.error?.includes('popup-closed-by-user') || result.error?.includes('cancelled')) {
                    // console.log( 'User cancelled sign-in' )
                    setError("Sign-in cancelled. Please don't close the popup.")
                } else {
                    setError(result.error || 'Google sign-in failed. Please allow popups.')
                }
                setIsSigningIn(false)
            }
        } catch (err: any) {
            console.error('Google sign-in error:', err)
            setError(err.message || 'Google sign-in failed')
            setIsSigningIn(false)
        }
    }

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!email || !password) {
            setError('Please fill in all fields')
            return
        }

        if (isSignUp && !displayName) {
            setError('Please enter your name')
            return
        }

        try {
            setIsSigningIn(true)
            setError(null)

            const result = isSignUp
                ? await signUpWithEmail(email, password, displayName)
                : await signInWithEmail(email, password)

            if (result.success && result.user && result.userData) {
                // Check if user is approved or admin
                if (result.userData.status !== UserStatus.ACTIVE && result.userData.role !== 'admin') {
                    setError('Your account is pending approval. Please contact the administrator.')
                    // Don't return here, let the UI handle the pending state on re-render/redirect check
                } else {
                    navigate({
                        to: redirect,
                        replace: true,
                    })
                }
            } else {
                setError(result.error || `${isSignUp ? 'Sign up' : 'Sign in'} failed`)
            }
        } catch (err: any) {
            console.error('Email auth error:', err)
            setError(err.message || `${isSignUp ? 'Sign up' : 'Sign in'} failed`)
        } finally {
            setIsSigningIn(false)
        }
    }

    // --- Render Logic (State: Pending Approval) ---
    if (isAuthenticated && userData) {
        const isPendingOrRejected = userData.status !== UserStatus.ACTIVE && userData.role !== 'admin'

        if (isPendingOrRejected) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-slate-950 p-4 transition-colors">
                    <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-lg w-full max-w-md text-center border border-slate-200 dark:border-slate-800">
                        <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock size={32} />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-2">Account Pending</h2>
                        <p className="text-gray-600 dark:text-slate-400 mb-6">
                            Your account is currently awaiting administrator approval. You will receive an email once
                            your access is granted.
                        </p>
                        <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 mb-6 text-sm text-slate-500 dark:text-slate-300 premium-cell-mono">
                            ID: {userData.email}
                        </div>
                        <button
                            onClick={() => signOut()}
                            className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline cursor-pointer"
                        >
                            Sign Out
                        </button>
                    </div>
                </div>
            )
        }

        // Re-routing handled by useEffect, show spinner
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin"></div>
                <p className="mt-4 text-slate-400 font-medium text-sm animate-pulse">Redirecting to Dashboard...</p>
            </div>
        )
    }

    // --- Render Logic (Standard Login) ---
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-slate-950 p-4 transition-colors duration-300">
            {/* Background Effects */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden opacity-50 dark:opacity-20">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-200/20 dark:bg-indigo-900/20 rounded-full blur-[120px]" />
                <div className="absolute top-[20%] right-[-10%] w-[30%] h-[30%] bg-purple-200/20 dark:bg-purple-900/20 rounded-full blur-[100px]" />
            </div>

            <button
                onClick={() => navigate({ to: '/' })}
                className="absolute top-4 left-4 flex items-center gap-2 text-gray-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors z-10"
            >
                <ArrowLeft size={20} /> Back to Home
            </button>

            <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-lg w-full max-w-md relative z-10 border border-slate-200 dark:border-slate-800">
                <h2 className="text-2xl font-bold mb-6 text-center text-gray-800 dark:text-slate-100">
                    {isSignUp ? 'Create Account' : 'Welcome Back'}
                </h2>

                {error && (
                    <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded mb-4 text-sm border border-red-200 dark:border-red-800">
                        {error}
                    </div>
                )}

                {/* Google Sign-In Button */}
                <button
                    onClick={handleGoogleSignIn}
                    disabled={isSigningIn || loading}
                    className="w-full flex items-center justify-center gap-3 bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-200 px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-400 dark:hover:border-slate-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-6 group"
                >
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                        <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                    {isSigningIn ? 'Signing in...' : 'Sign in with Google'}
                </button>

                {/* Fallback for blocked popups */}
                <div className="text-center mb-6">
                    <button
                        onClick={() => {
                            setIsSigningIn(true)
                            signInWithGoogleRedirect()
                        }}
                        disabled={isSigningIn || loading}
                        className="text-xs text-gray-400 dark:text-slate-500 underline hover:text-gray-600 dark:hover:text-slate-400"
                    >
                        (Problems with popup? Click here to redirect)
                    </button>
                </div>

                <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-slate-700"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-slate-900 text-gray-500 dark:text-slate-500">
                            Or continue with email
                        </span>
                    </div>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={handleEmailAuth} className="space-y-4">
                    {isSignUp && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Full Name
                            </label>
                            <div className="relative">
                                <UserIcon
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                    size={18}
                                />
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                    placeholder="John Doe"
                                    disabled={isSigningIn || loading}
                                    autoComplete="name"
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                            Email Address
                        </label>
                        <div className="relative">
                            <Mail
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                size={18}
                            />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                placeholder="you@example.com"
                                disabled={isSigningIn || loading}
                                autoComplete="email"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                            Password
                        </label>
                        <div className="relative">
                            <Lock
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                size={18}
                            />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                placeholder="••••••••"
                                disabled={isSigningIn || loading}
                                minLength={6}
                                autoComplete="current-password"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isSigningIn || loading}
                        className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                    >
                        {isSigningIn || loading ? 'Please wait...' : isSignUp ? 'Sign Up' : 'Sign In'}
                    </button>
                </form>

                <div className="mt-6 text-center">
                    <button
                        onClick={() => {
                            setIsSignUp(!isSignUp)
                            setError(null)
                        }}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                        disabled={isSigningIn || loading}
                    >
                        {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
                    </button>
                </div>

                <div className="mt-4 text-center text-xs text-gray-500 dark:text-gray-500">
                    <p>Powered by Firebase Authentication</p>
                </div>
            </div>
        </div>
    )
}
